import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Elements } from '@stripe/react-stripe-js'
import { type StripeElementsOptions } from '@stripe/stripe-js'
import { AlertCircle, CheckCircle2, CreditCard, Loader2, ReceiptText } from 'lucide-react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  StripeInvoicePaymentForm,
  getStripePromise,
  normalizePublicImageUrl,
  readString,
  toNumber,
  toRecord,
  type StripeIntentState,
} from '@/components/finance/public-payment-shared'
import { financeApi } from '@/services/finance'
import { getErrorMessage } from '@/lib/api'
import { formatCurrency, formatDate } from '@/lib/utils'

// Job payment link (/job/pay/:paymentToken): lets the customer pay a job before it is
// completed or invoiced. The link may carry a fixed partial amount set by staff.
export default function PublicJobPaymentPage() {
  const { paymentToken } = useParams<{ paymentToken: string }>()
  const [searchParams] = useSearchParams()
  const [stripeIntent, setStripeIntent] = useState<StripeIntentState | null>(null)
  const [webhookPendingReference, setWebhookPendingReference] = useState<string | null>(null)
  const handledReturnPaymentIntent = useRef<string | null>(null)
  const paymentStatus = searchParams.get('payment_status')
  const reference = searchParams.get('reference')
  const token = useMemo(() => (paymentToken ? decodeURIComponent(paymentToken) : ''), [paymentToken])
  const returnedPaymentIntent = searchParams.get('payment_intent')

  const paymentQuery = useQuery({
    queryKey: ['public-job-payment', token],
    queryFn: () => financeApi.getPublicJobPaymentTokenDetail(token),
    enabled: Boolean(token),
    retry: false,
  })

  const job = paymentQuery.data?.job
  const jobContext = toRecord(job)
  const serviceInstanceId = (searchParams.get('org_instance_id') || '').trim()

  const serviceInstanceBrandingQuery = useQuery({
    queryKey: ['public-service-instance-branding', serviceInstanceId],
    queryFn: () => financeApi.getPublicServiceInstanceBranding(serviceInstanceId),
    enabled: Boolean(serviceInstanceId),
    retry: false,
  })
  const serviceInstanceBranding = toRecord(serviceInstanceBrandingQuery.data)

  const displayLogo = useMemo(() => (
    normalizePublicImageUrl(readString(jobContext, ['organization_logo_url', 'organization_logo', 'branch_logo_url', 'branch_logo']))
    || normalizePublicImageUrl(readString(serviceInstanceBranding, ['welcome_page_logo', 'home_page_logo', 'login_page_logo', 'side_panel_logo', 'logo']))
  ), [jobContext, serviceInstanceBranding])
  const organizationName = readString(serviceInstanceBranding, ['parent_instance_name', 'organization_name', 'organisation_name']) || 'Organization'
  const branchName = readString(serviceInstanceBranding, ['name', 'instance_name'])

  const initiatePaymentMutation = useMutation({
    mutationFn: () => financeApi.initiateJobGatewayPaymentByToken(token),
    onSuccess: (data) => {
      if (data.payment_link) {
        window.location.assign(data.payment_link)
        return
      }
      if (data.signature && data.publishable_key) {
        setStripeIntent({
          publishableKey: data.publishable_key,
          clientSecret: data.signature,
          paymentReferenceId: data.payment_reference_id || '',
          walletId: data.wallet?.id,
        })
        return
      }
      toast.info('Payment gateway initiated.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const total = toNumber(job?.total)
  const paidAmount = toNumber(job?.paid_amount)
  const balanceDue = toNumber(job?.payment_due)
  const amountDue = toNumber(paymentQuery.data?.amount_due)
  const canPay = Boolean(paymentQuery.data?.can_pay ?? amountDue > 0)
  const isPaid = Boolean(paymentQuery.data?.paid)
  const confirmedPaymentReference = reference || paymentQuery.data?.payment_reference || webhookPendingReference

  const stripePromise = useMemo(
    () => (stripeIntent?.publishableKey ? getStripePromise(stripeIntent.publishableKey) : null),
    [stripeIntent?.publishableKey],
  )
  const stripeElementsOptions = useMemo<StripeElementsOptions | undefined>(
    () => (stripeIntent?.clientSecret ? { clientSecret: stripeIntent.clientSecret, appearance: { theme: 'stripe' } } : undefined),
    [stripeIntent?.clientSecret],
  )

  // The payment is recorded by the gateway webhook, so poll until the balance moves.
  const waitForWebhookConfirmation = async (paymentReferenceId: string) => {
    setWebhookPendingReference(paymentReferenceId)
    toast.info('Payment completed. Waiting for gateway confirmation.')
    const startingBalance = toNumber(paymentQuery.data?.job?.payment_due)

    for (let attempt = 0; attempt < 12; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 1500 : 2500))
      const result = await paymentQuery.refetch()
      const refreshedBalance = toNumber(result.data?.job?.payment_due)
      if (result.data?.paid || refreshedBalance < startingBalance - 0.005) {
        setStripeIntent(null)
        setWebhookPendingReference(null)
        toast.success(paymentReferenceId ? `Payment confirmed. Reference ${paymentReferenceId}.` : 'Payment confirmed.')
        return
      }
    }

    toast.info('Payment completed. Gateway confirmation is still processing.')
  }

  useEffect(() => {
    if (!token || !returnedPaymentIntent || handledReturnPaymentIntent.current === returnedPaymentIntent) {
      return
    }
    handledReturnPaymentIntent.current = returnedPaymentIntent
    waitForWebhookConfirmation(returnedPaymentIntent).catch((error) => toast.error(getErrorMessage(error)))
  }, [returnedPaymentIntent, token])

  const statusNotice = useMemo(() => {
    if (paymentStatus === 'success') {
      return {
        className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
        icon: <CheckCircle2 className="h-4 w-4" />,
        text: confirmedPaymentReference ? `Payment received. Reference ${confirmedPaymentReference}.` : 'Payment received.',
      }
    }
    if (isPaid) {
      return {
        className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
        icon: <CheckCircle2 className="h-4 w-4" />,
        text: paymentQuery.data?.message || 'Payment is already paid.',
      }
    }
    if (paymentStatus === 'failed') {
      return {
        className: 'border-red-200 bg-red-50 text-red-800',
        icon: <AlertCircle className="h-4 w-4" />,
        text: reference ? `Payment was not completed. Reference ${reference}.` : 'Payment was not completed.',
      }
    }
    return null
  }, [paymentStatus, reference, isPaid, paymentQuery.data?.message, confirmedPaymentReference])

  if (paymentQuery.isLoading) {
    return (
      <main className="min-h-screen bg-muted/30 px-4 py-8">
        <div className="mx-auto max-w-3xl space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-52 w-full" />
          <Skeleton className="h-36 w-full" />
        </div>
      </main>
    )
  }

  if (paymentQuery.isError || !job) {
    return (
      <main className="min-h-screen bg-muted/30 px-4 py-8">
        <div className="mx-auto max-w-3xl rounded-lg border bg-background p-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <h1 className="text-lg font-semibold">Payment link not available</h1>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(paymentQuery.error)}</p>
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-muted/30 px-4 py-8">
      <div className="mx-auto max-w-3xl space-y-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md border bg-background">
            <ReceiptText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Payment</h1>
            <p className="text-sm text-muted-foreground">{job.title || `Job ${job.id}`}</p>
          </div>
        </div>

        {displayLogo ? (
          <div className="rounded-xl border border-border/70 bg-card/60 px-4 py-5">
            <div className="mx-auto flex max-w-xl flex-col items-center gap-2 text-center">
              <img src={displayLogo} alt={`${organizationName} logo`} className="h-14 w-14 rounded object-contain" />
              <p className="text-base font-semibold text-foreground">{organizationName}</p>
            </div>
            {branchName ? <p className="mt-3 text-left text-xs text-muted-foreground">{branchName}</p> : null}
          </div>
        ) : null}

        {statusNotice ? (
          <div className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${statusNotice.className}`}>
            {statusNotice.icon}
            <span>{statusNotice.text}</span>
          </div>
        ) : null}

        {webhookPendingReference ? (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Payment completed. Waiting for gateway confirmation.</span>
          </div>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase text-muted-foreground">Job</p>
              <p className="font-medium">{job.title || `Job ${job.id}`}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Customer</p>
              <p className="font-medium">{job.customer_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Scheduled</p>
              <p className="font-medium">{job.scheduled_start ? formatDate(job.scheduled_start) : '-'}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Payment Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Total so far</span>
              <span className="font-medium">{formatCurrency(total)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Paid</span>
              <span className="font-medium">{formatCurrency(paidAmount)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Balance</span>
              <span className="font-medium">{formatCurrency(balanceDue)}</span>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <span className="font-medium">Amount to pay</span>
              <span className="text-xl font-semibold">{formatCurrency(amountDue)}</span>
            </div>
            <Button
              className="mt-3 w-full"
              size="lg"
              disabled={!canPay || initiatePaymentMutation.isPending || Boolean(stripeIntent) || Boolean(webhookPendingReference)}
              onClick={() => initiatePaymentMutation.mutate()}
            >
              {initiatePaymentMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
              {canPay ? 'Make Payment' : 'Paid'}
            </Button>
          </CardContent>
        </Card>

        {stripeIntent && stripePromise && stripeElementsOptions ? (
          <Card>
            <CardHeader>
              <CardTitle>Card Payment</CardTitle>
            </CardHeader>
            <CardContent>
              <Elements key={stripeIntent.clientSecret} stripe={stripePromise} options={stripeElementsOptions}>
                <StripeInvoicePaymentForm
                  paymentReferenceId={stripeIntent.paymentReferenceId}
                  onPaymentSucceeded={waitForWebhookConfirmation}
                />
              </Elements>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </main>
  )
}
